import React, { useEffect, useRef, useState } from 'react';
import { usePortalCopy } from '../PortalUi';
import { isWeekendDay } from './mxHolidays';

/**
 * Day picker for the Olé appointment modal only (video interview and Olé
 * medical exam). Saturday and Sunday are not selectable. Hours stay on the
 * sibling `<select>`. Other date fields in the app keep the native picker.
 */

const WEEKDAYS_ES = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'] as const;
const MONTHS_ES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
] as const;
const DOW_HEADERS = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá', 'Do'] as const;

type Props = {
  id?: string;
  value: string;
  min: string;
  max: string;
  disabled?: boolean;
  dataSpec?: string;
  onChange: (day: string) => void;
};

export function formatScheduleDay(day: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return '';
  return `${match[3]}/${match[2]}/${match[1]}`;
}

/** Accessible name, stable (no Intl): "sábado 17 de octubre de 2026". */
export function scheduleDayName(day: string): string {
  const [year, month, date] = day.split('-').map(Number);
  const utc = new Date(Date.UTC(year, (month ?? 1) - 1, date ?? 1));
  if (Number.isNaN(utc.getTime())) return day;
  const weekday = WEEKDAYS_ES[utc.getUTCDay()];
  const monthName = MONTHS_ES[(month ?? 1) - 1];
  return `${weekday} ${date} de ${monthName} de ${year}`;
}

function monthOf(day: string): string {
  return /^\d{4}-\d{2}/.test(day) ? day.slice(0, 7) : '';
}

function shiftMonth(ym: string, delta: number): string {
  const [year, month] = ym.split('-').map(Number);
  const date = new Date(Date.UTC(year, (month ?? 1) - 1 + delta, 1));
  const nextMonth = `${date.getUTCMonth() + 1}`.padStart(2, '0');
  return `${date.getUTCFullYear()}-${nextMonth}`;
}

function monthTitle(ym: string): string {
  const [year, month] = ym.split('-').map(Number);
  const name = MONTHS_ES[(month ?? 1) - 1] ?? '';
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${year}`;
}

function lastDayOf(ym: string): string {
  const [year, month] = ym.split('-').map(Number);
  const date = new Date(Date.UTC(year, month ?? 1, 0));
  const day = `${date.getUTCDate()}`.padStart(2, '0');
  return `${date.getUTCFullYear()}-${`${date.getUTCMonth() + 1}`.padStart(2, '0')}-${day}`;
}

/** Monday-first month grid, including the leading/trailing days of the adjacent months. */
function monthCells(ym: string): string[] {
  const [year, month] = ym.split('-').map(Number);
  const first = new Date(Date.UTC(year, (month ?? 1) - 1, 1));
  const mondayOffset = (first.getUTCDay() + 6) % 7;
  const start = new Date(Date.UTC(year, (month ?? 1) - 1, 1 - mondayOffset));
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start);
    date.setUTCDate(start.getUTCDate() + index);
    const day = `${date.getUTCDate()}`.padStart(2, '0');
    const cellMonth = `${date.getUTCMonth() + 1}`.padStart(2, '0');
    return `${date.getUTCFullYear()}-${cellMonth}-${day}`;
  });
}

function isSelectable(day: string, min: string, max: string): boolean {
  if (isWeekendDay(day)) return false;
  if (min && day < min) return false;
  if (max && day > max) return false;
  return true;
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3.5" y="5" width="17" height="15" rx="2" />
      <path d="M3.5 10h17M8 3.5v4M16 3.5v4" strokeLinecap="round" />
    </svg>
  );
}

export function ScheduleDayField({ id, value, min, max, disabled, dataSpec, onChange }: Props) {
  const t = usePortalCopy();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => monthOf(value) || monthOf(min));

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openCalendar = () => {
    if (disabled) return;
    setMonth(monthOf(value) || monthOf(min));
    setOpen((current) => !current);
  };

  const prevMonth = shiftMonth(month, -1);
  const nextMonth = shiftMonth(month, 1);
  const prevDisabled = Boolean(min) && lastDayOf(prevMonth) < min;
  const nextDisabled = Boolean(max) && `${nextMonth}-01` > max;
  const weekend = Boolean(value) && isWeekendDay(value);

  return (
    // The parent is a <label>. Clicks inside this picker must not bubble, or
    // the label re-activates the trigger and the calendar toggles shut.
    <div className="sched-day" ref={rootRef} onClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        id={id}
        className={`inp sched-day-btn${value ? '' : ' is-empty'}${weekend ? ' bad' : ''}`}
        aria-label={value ? `${t('scheduleDay')}, ${formatScheduleDay(value)}` : t('scheduleDay')}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        data-spec={dataSpec}
        data-min={min}
        data-max={max}
        onClick={openCalendar}
      >
        <span>{value ? formatScheduleDay(value) : 'dd/mm/aaaa'}</span>
        <CalendarIcon />
      </button>
      {open ? (
        <div className="sched-cal" role="dialog" aria-label={t('schedulePickDay')}>
          <div className="sched-cal-nav">
            <button
              type="button"
              aria-label={t('schedulePrevMonth')}
              disabled={prevDisabled}
              onClick={() => setMonth(prevMonth)}
            >
              ‹
            </button>
            <strong>{monthTitle(month)}</strong>
            <button
              type="button"
              aria-label={t('scheduleNextMonth')}
              disabled={nextDisabled}
              onClick={() => setMonth(nextMonth)}
            >
              ›
            </button>
          </div>
          <div className="sched-cal-grid">
            {DOW_HEADERS.map((header) => (
              <span className="sched-cal-dow" key={header}>{header}</span>
            ))}
            {monthCells(month).map((day) => {
              const selectable = isSelectable(day, min, max);
              const inMonth = day.slice(0, 7) === month;
              const selected = day === value;
              return (
                <button
                  type="button"
                  key={day}
                  className={[
                    'sched-cal-day',
                    inMonth ? '' : 'is-out',
                    isWeekendDay(day) ? 'is-weekend' : '',
                    selected ? 'is-selected' : '',
                  ].filter(Boolean).join(' ')}
                  aria-label={scheduleDayName(day)}
                  aria-pressed={selected}
                  disabled={!selectable}
                  onClick={() => {
                    if (!selectable) return;
                    onChange(day);
                    setOpen(false);
                  }}
                >
                  {Number(day.slice(8, 10))}
                </button>
              );
            })}
          </div>
          <p className="sched-cal-note">{t('scheduleWeekendsClosed')}</p>
        </div>
      ) : null}
    </div>
  );
}
